<template>
  <div class="calcFormsForm">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div v-else>
      <div class="tableErrorContainer">
        <v-scroll-y-transition>
          <div v-if="!isTableValid" class="v-messages error--text tableError">
            Name, date and value columns are mandatory
          </div>
        </v-scroll-y-transition>
      </div>
      <v-simple-table class="mb-8">
        <tr>
          <th class="bd-r bd-b">Element name</th>
          <th class="bd-r bd-b">Calculation date</th>
          <th class="bd-r bd-b">Plotted value</th>
          <th class="bd-r bd-b">Plotted cash</th>
          <th class="bd-r bd-b">Commutation factor</th>
          <th class="bd-b"></th>
        </tr>
        <tr v-for="(item, index) in elementsItems" :key="index">
          <td class="bd-r bd-b px-4" @click="openNameDialog(item)">
            {{ item.PlotName }}
          </td>
          <td class="bd-r bd-b" @click="openCalculationSelection(index, 'PlotDate')">
            {{ item.PlotDate }}
          </td>
          <td class="bd-r bd-b" @click="openCalculationSelection(index, 'PlotValue')">
            {{ item.PlotValue }}
          </td>
          <td class="bd-r bd-b" @click="openCalculationSelection(index, 'PlotCash')">
            {{ item.PlotCash }}
          </td>
          <td class="bd-r bd-b" @click="openCalculationSelection(index, 'CommutationFactor')">
            {{ item.CommutationFactor }}
          </td>
          <td class="bd-b" @click="onRowDelete(index)">
            <v-icon :disabled="!showFormButtons">mdi-delete</v-icon>
          </td>
        </tr>
      </v-simple-table>

      <div v-if="showFormButtons" class="formFooter">
        <div class="btnContainer">
          <!-- <v-btn v-if="elementsId" color="error" outlined @click="onDelete" class="mr-4">
            Delete
          </v-btn> -->
          <v-btn color="primary" outlined @click="onAddRow" class="mr-4"> Add row </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="onCancel" class="mr-4"> Cancel </v-btn>
          <v-btn color="primary" depressed :disabled="!isTableValid" @click="onSave">
            {{ elementsId ? 'Save' : 'Save new' }}
          </v-btn>
        </div>
      </div>
    </div>

    <Dialog ref="confirmationDialog" v-model="dialog"></Dialog>
    <CalculationSelection
      ref="calculationSelection"
      :levelId="planId"
      :typeList="typeList"
      @select="onSelect"
    />

    <v-dialog v-model="nameDialog" @keydown.esc="cancel" persistent width="500">
      <v-card>
        <div class="px-4 pt-8">
          <v-form>
            <div>
              <v-select
                v-model="selectedElement.PlotName"
                label="Element name"
                :items="availableNames"
                item-text="ShortName"
                item-value="ShortName"
                outlined
                dense
              ></v-select>
            </div>
          </v-form>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="onNameCancel"> Cancel </v-btn>
          <v-btn color="primary" depressed @click.native="onNameSave"> Ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import CalculationSelection from './Popups/CalculationSelection.vue';

export default {
  name: 'ElementsForm',
  components: {
    Dialog,
    CalculationSelection,
  },
  props: {
    elementsId: { type: Number },
    planId: { type: Number },
    groupId: { type: Number, default: 0 },
  },

  data() {
    return {
      loading: false,
      dialog: false,
      nameDialog: false,

      elementsItems: [],
      availableNames: [],

      selectedElement: {},
      selectedIndex: null,
      typeList: null,

      calculationSelectionProperty: null,

      rules: [(value) => !!value || 'Required.'],
    };
  },

  computed: {
    levelsList() {
      return [
        { text: 'System', value: 0 },
        { text: 'Plan', value: this.currentPlanId },
      ];
    },
    isTableValid() {
      return this.elementsItems.every((item) => item.PlotName && item.PlotDate && item.PlotValue);
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    elementsId: function () {
      if (this.elementsId) this.getForm();
      else this.createNewForm();
    },
    formName() {
      if (this.formName) this.getFieldsList();
    },
  },

  created() {
    if (this.elementsId) {
      this.getForm();
    } else {
      this.createNewForm();
    }
    this.getElementNamesList();
  },

  methods: {
    createNewForm() {
      this.elementsItems = [
        {
          ExitPlotId: 0,
          ElementId: 0,
          PlotDate: '',
          PlotValue: '',
          PlotCash: '',
          CommutationFactor: '',
          PlotName: '',
          Contingent: false,
        },
      ];
    },

    openNameDialog(item) {
      if (!this.showFormButtons) return;
      this.selectedElement = { ...item };
      this.selectedIndex = this.elementsItems.indexOf(item);
      this.nameDialog = true;
    },

    onNameCancel() {
      this.nameDialog = false;
      this.selectedElement = {};
    },

    onNameSave() {
      this.elementsItems.splice(this.selectedIndex, 1, this.selectedElement);
      this.nameDialog = false;
      this.selectedElement = {};
    },

    onCancel() {
      if (this.elementsId) this.getForm();
      else this.createNewForm();
    },

    onSave() {
      if (this.isTableValid) this.updateForm();
    },

    openCalculationSelection(index, property) {
      if (!this.showFormButtons) return;
      if (property === 'PlotDate') {
        this.typeList = ['Inputs','Date'];
      } else {
        this.typeList = null;
      }
      this.selectedIndex = index;
      this.calculationSelectionProperty = property;
      this.$refs.calculationSelection.open();
    },

    onSelect(val) {
      this.elementsItems[this.selectedIndex][this.calculationSelectionProperty] = val;
      this.calculationSelectionProperty = null;
      this.selectedIndex = null;
    },

    onAddRow() {
      this.elementsItems.push({
        ExitPlotId: this.elementsId,
        ElementId: 0,
        PlotDate: '',
        PlotValue: '',
        PlotCash: '',
        CommutationFactor: '',
        PlotName: '',
        Contingent: false,
      });
    },

    async onRowDelete(index) {
      if (!this.showFormButtons) return;
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this row?'
        );
        this.elementsItems.splice(index, 1);
      } catch (err) {
        console.log(err);
      }
    },

    async getForm() {
      this.loading = true;
      this.elementsItems = [];
      try {
        let response = await this.$api.get(
          '/CalculationElements/GetPlottingElements?plotId=' + this.elementsId
        );
        var data = response.data;
        this.elementsItems = data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    async updateForm() {
      console.log(this.elementsItems);
      try {
        let response = await this.$api.post(
          '/CalculationElements/SavePlottedElements?plotId=' + this.elementsId,
          this.elementsItems
        );

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Elements were saved',
        });
        if (this.elementsItems.ExitPlotId === 0) {
          this.$emit('reloadList', 'update', response.data.Value);
        } else {
          this.$emit('reloadList', 'update');
          this.getForm();
        }
      } catch (err) {
        console.error(err);
      }
    },

    async getElementNamesList() {
      try {
        let response = await this.$api.get(
          `/Calculation/GetCalculationItems?planId=${this.planId}&calcType=Pension Elements&groupId=${this.groupId}`
        );
        var data = response.data;
        this.availableNames = data;
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calcFormsForm {
  th {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    height: 32px;
  }
  td {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.87);
    text-align: center;
    height: 32px;
    padding: 0 8px;
  }
  tr:nth-child(n + 2) td:hover {
    cursor: pointer;
    background-color: #f2f2f2;
  }
  .bd-x {
    border-left: thin solid rgba(0, 0, 0, 0.12);
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-l {
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-r {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-b {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }
  .bd-t {
    border-top: thin solid rgba(0, 0, 0, 0.12);
  }
  .tableErrorContainer {
    height: 28px;
  }
  .tableError {
    margin-bottom: 10px;
  }
}
</style>
